import { useModelExporter } from '#imports';
import { StepBoxReveal, StepIdle, StepPackReveal } from '@fanatics/resn-api/instant-rips';
import gsap from 'gsap';
import { defineComponent, inject, onMounted, provide, ref, watch, watchEffect } from 'vue';

import { lerp, toRadians } from '@resn/gozer-math';
import { basePaneOptions, usePane, useResize, useThreeObject } from '@resn/gozer-vue';

import { usePerspectiveCamera } from '~/composables/useThreeCamera';

export const useSceneCamera = () => {
    const sceneCamera = inject('sceneCamera');

    if (!sceneCamera) {
        throw new Error('SceneCamera provider not found');
    }

    return sceneCamera;
};

const SceneCamera = defineComponent({
    props: {
        fov: { type: Number, default: 50 },
        zoom: { type: Number, default: 1 },
        introProgress: { type: Number, default: 1 },
        openProgress: { type: Number, default: 1 },
    },

    setup(props) {
        const { object } = useThreeObject(null, { nesting: false, name: 'SceneCameraObject' });

        const camera = usePerspectiveCamera();
        camera.name = 'SceneCamera';

        const introProgress = ref(props.introProgress);
        const openProgress = ref(props.openProgress);

        object.add(camera);

        const setPosition = (position) => {
            camera.position.set(position.x, position.y, position.z);
        };

        const setStep = (val) => {
            switch (val) {
                case StepIdle:
                    reset();
                    break;
                case StepBoxReveal:
                    show();
                    break;
                case StepPackReveal:
                    open();
                    break;

                default:
                    snapToEndState();
                    break;
            }
        };

        const updateTweens = () => {
            let z = lerp(introProgress.value, 8, 7);

            const rx = toRadians(lerp(openProgress.value, -25, 0));
            z = lerp(openProgress.value, z, 2.8);

            camera.position.set(0, 0, z);
            object.rotation.set(rx, 0, 0);

            camera.lookAt(0, 0, 0);
        };

        let tlShow;
        const show = () => {
            killTweens();

            tlShow = gsap.timeline({ onComplete: updateTweens });
            tlShow.set(openProgress, { value: 0 });
            tlShow.fromTo(
                introProgress,
                { value: 0 },
                { value: 1, duration: 1, ease: 'power2.out' }
            );
        };

        let tlOpen;
        const open = () => {
            killTweens();

            tlOpen = gsap.timeline({ onComplete: updateTweens });
            tlOpen.fromTo(
                openProgress,
                { value: 0 },
                { value: 1, duration: 1, ease: 'power2.out' }
            );
        };

        const snapToEndState = () => {
            killTweens();

            introProgress.value = openProgress.value = 1;

            updateTweens();
        };

        onMounted(updateTweens);

        const reset = () => {
            killTweens();

            introProgress.value = openProgress.value = 0;

            updateTweens();
        };

        const killTweens = () => {
            tlShow?.kill();
            tlOpen?.kill();
        };

        watch([introProgress, openProgress], updateTweens);

        watchEffect(() => {
            camera.fov = props.fov;
            camera.zoom = props.zoom;

            camera.updateProjectionMatrix();
        });

        useResize(({ width, height }) => {
            camera.aspect = width / height || 1;
            camera.updateProjectionMatrix();
        });

        const providerObj = {
            camera,
            setPosition,
            show,
            open,
            reset,
            setStep,
        };

        provide('sceneCamera', providerObj);

        const modelExport = useModelExporter();
        if (!modelExport) {
            usePane(
                [
                    { name: 'introProgress', value: introProgress, options: basePaneOptions },
                    { name: 'openProgress', value: openProgress, options: basePaneOptions },
                ],
                {
                    title: 'SceneCamera',
                    expanded: false,
                    subfolder: false,
                }
            );
        }

        return providerObj;
    },

    render() {
        return this.$slots.default ? this.$slots.default() : null;
    },
});

export default SceneCamera;
